import { createTheme, alpha } from '@mui/material/styles';

// Electric Blue accent color
const PRIMARY_COLOR = '#007FFF';
const SECONDARY_COLOR = '#00D4AA';

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: PRIMARY_COLOR,
            light: '#4DA3FF',
            dark: '#0059B2',
            contrastText: '#ffffff',
        },
        secondary: {
            main: SECONDARY_COLOR,
            light: '#33DDB8',
            dark: '#00A385',
            contrastText: '#000000',
        },
        background: {
            default: '#0a0a0f',
            paper: '#12121a',
        },
        text: {
            primary: '#ffffff',
            secondary: alpha('#ffffff', 0.7),
        },
        divider: alpha('#ffffff', 0.1),
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontSize: '3.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontSize: '2.5rem',
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: '2rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: 1.5,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.5,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.7,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.6,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '*': {
                    scrollBehavior: 'smooth',
                },
                body: {
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${alpha(PRIMARY_COLOR, 0.3)} transparent`,
                    '&::-webkit-scrollbar': {
                        width: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(PRIMARY_COLOR, 0.3),
                        borderRadius: 4,
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    padding: '10px 24px',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease',
                },
                contained: {
                    boxShadow: `0 4px 14px 0 ${alpha(PRIMARY_COLOR, 0.4)}`,
                    '&:hover': {
                        boxShadow: `0 6px 20px 0 ${alpha(PRIMARY_COLOR, 0.5)}`,
                        transform: 'translateY(-2px)',
                    },
                },
                outlined: {
                    borderWidth: 2,
                    '&:hover': {
                        borderWidth: 2,
                        backgroundColor: alpha(PRIMARY_COLOR, 0.1),
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha('#ffffff', 0.03),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha('#ffffff', 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${alpha('#000000', 0.3)}`,
                        border: `1px solid ${alpha(PRIMARY_COLOR, 0.3)}`,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderRadius: 6,
                },
                filled: {
                    backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                    color: PRIMARY_COLOR,
                    '&:hover': {
                        backgroundColor: alpha(PRIMARY_COLOR, 0.25),
                    },
                },
                outlined: {
                    borderColor: alpha(PRIMARY_COLOR, 0.5),
                    color: PRIMARY_COLOR,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
    },
});

// Custom CSS variables for use in styled components
export const cssVariables = {
    primaryGlow: `0 0 40px ${alpha(PRIMARY_COLOR, 0.4)}`,
    secondaryGlow: `0 0 40px ${alpha(SECONDARY_COLOR, 0.4)}`,
    gradientPrimary: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
    gradientBackground: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0a0a0f 100%)',
    glassBg: alpha('#ffffff', 0.05),
    glassBorder: alpha('#ffffff', 0.1),
};

export default theme;
